import ReactGA from "react-ga4";
import Clarity from "@microsoft/clarity";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;

let isInitialized = false;

/**
 * Public customer email pages carry a signed bearer token in the URL. They must
 * never be recorded by GA4/Clarity (page_location and session replays would
 * capture the token).
 */
const SENSITIVE_PATH_PREFIXES = ["/temporary-access"];

export function isSensitivePath(pathname: string): boolean {
  return SENSITIVE_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

type ClarityCommand = (command: "start" | "stop") => void;
let isSuppressed = false;

/**
 * Called on every route change. Entering a sensitive page stops Clarity
 * recording and disables GA4 (its history-change page_view would otherwise
 * capture the URL); leaving it resumes both. Without this, an SPA session
 * that started on a portal page kept recording after navigating to one.
 */
export function syncAnalyticsWithRoute(pathname: string): void {
  if (typeof window === "undefined") return;
  const shouldSuppress = isSensitivePath(pathname);
  if (!isInitialized) {
    // The session may have started on a sensitive page, where initAnalytics()
    // bails out. Initialise on the first non-sensitive route instead.
    if (!shouldSuppress) initAnalytics(pathname);
    return;
  }
  if (shouldSuppress === isSuppressed) return;
  isSuppressed = shouldSuppress;

  if (GA_MEASUREMENT_ID) {
    (window as unknown as Record<string, unknown>)[
      `ga-disable-${GA_MEASUREMENT_ID}`
    ] = shouldSuppress;
  }
  const clarity = (window as unknown as { clarity?: ClarityCommand }).clarity;
  clarity?.(shouldSuppress ? "stop" : "start");
}

/**
 * Test-only: simulate an initialised SDK without a production build.
 * @public (used via `jest.requireActual` in analytics.test.ts)
 */
export function __setAnalyticsInitializedForTests(value: boolean): void {
  isInitialized = value;
  isSuppressed = false;
}

export function initAnalytics(
  pathname: string | undefined = typeof window !== "undefined"
    ? window.location.pathname
    : undefined,
): void {
  if (process.env.NODE_ENV !== "production" || isInitialized) return;
  if (pathname !== undefined && isSensitivePath(pathname)) return;

  if (!GA_MEASUREMENT_ID || !CLARITY_PROJECT_ID) {
    console.warn(
      "Analytics initialization skipped: REACT_APP_GA_MEASUREMENT_ID and REACT_APP_CLARITY_PROJECT_ID must be configured in production.",
    );
    return;
  }

  ReactGA.initialize(GA_MEASUREMENT_ID);
  Clarity.init(CLARITY_PROJECT_ID);
  isInitialized = true;
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
): void {
  if (!isInitialized) return;

  ReactGA.event(eventName, params);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      Clarity.setTag(key, String(value));
    });
  }

  Clarity.event(eventName);
}

export function trackPageView(path: string, title?: string): void {
  if (!isInitialized) return;
  if (isSensitivePath(path.split("?")[0])) return;

  ReactGA.send({ hitType: "pageview", page: path, title });
}

export function identifyUser(
  userId: string,
  traits: { name?: string; email?: string; role?: string },
): void {
  if (!isInitialized) return;

  ReactGA.set({ userId });
  ReactGA.set({
    user_properties: {
      role: traits.role,
    },
  });

  Clarity.identify(userId, undefined, undefined, traits.name);
  if (traits.role) {
    Clarity.setTag("role", traits.role);
  }
}

export function upgradeSession(reason: string): void {
  if (!isInitialized) return;

  Clarity.upgrade(reason);
}
